import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { successDefs, xpForNextLevel, type BotUser, type GameEvent, type Lang } from '@fakerpg/core';
import {
  Backpack,
  BarChart3,
  Dices,
  Egg,
  Gift,
  Hourglass,
  Settings as SettingsIcon,
  ScrollText,
  Shield,
  Sparkles,
  Swords,
  Tent,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import { useLang, useNow, usePrefs, useSave, useSession, useSnapshot, useStore, useT } from './game/context.js';
import { errorText, formatCompact, formatNumber, isLangLoaded, loadLang, translate, type MessageKey } from './i18n/index.js';
import { navigate, useRoute, type RouteName } from './router.js';
import { Badge, Countdown, Popover, Ring, Toasts } from './ui/components.js';
import { CountUp, LevelUpOverlay } from './ui/effects.js';
import { setSoundEnabled } from './ui/sfx.js';
import { Sprite } from './assets/Sprite.js';
import { PwaNotices } from './ui/PwaNotices.js';
import { Gate } from './screens/Gate.js';
import { Home } from './screens/Home.js';
import { lazyScreens, prefetchScreens } from './screens/lazy.js';
import { successPlainName } from './screens/successText.js';
import { leadCharacter, navBadges, useLiveEnergy } from './screens/shared.js';
import { missionEndsAt, missionStatus, nextEnergyAtLive } from '@fakerpg/core';

interface NavItem {
  name: RouteName;
  icon: LucideIcon;
  key: MessageKey;
}

const NAV_MAIN: NavItem[] = [
  { name: 'home', icon: Tent, key: 'nav.home' },
  { name: 'adventures', icon: Swords, key: 'nav.adventures' },
  { name: 'summon', icon: Sparkles, key: 'nav.summon' },
  { name: 'team', icon: Shield, key: 'nav.team' },
  { name: 'collection', icon: Backpack, key: 'nav.collection' },
];

const NAV_EXTRA: NavItem[] = [
  { name: 'missions', icon: Hourglass, key: 'nav.missions' },
  { name: 'daily', icon: Gift, key: 'nav.daily' },
  { name: 'quests', icon: ScrollText, key: 'nav.quests' },
  { name: 'attributes', icon: Egg, key: 'nav.attributes' },
  { name: 'games', icon: Dices, key: 'nav.games' },
  { name: 'success', icon: Trophy, key: 'nav.success' },
  { name: 'stats', icon: BarChart3, key: 'nav.stats' },
];

function toastFor(event: GameEvent, _user: BotUser, lang: Lang) {
  const n = (v: number) => formatNumber(lang, v);
  switch (event.type) {
    case 'itemUpgraded':
      return { text: translate(lang, 'toast.upgraded', { level: event.level }), icon: 'level' as const };
    case 'itemsUpgraded':
      return { text: translate(lang, 'toast.upgradedAll', { n: event.upgrades, price: n(event.spent) }), icon: 'level' as const };
    case 'missionRewards':
      return { text: translate(lang, 'toast.mission', { money: n(event.money), xp: n(event.xp), loot: n(event.lootbox) }), icon: 'coin' as const };
    case 'dailyClaimed':
      return { text: translate(lang, 'toast.daily', { day: event.day, money: n(event.money) }), icon: 'gift' as const };
    case 'questCompleted':
      return { text: translate(lang, 'quests.completed', { xp: event.reward }), icon: 'success' as const };
    case 'successUnlocked': {
      const def = successDefs.find((d) => d.id === event.id);
      return def ? { text: translate(lang, 'success.toast', { name: successPlainName(def, lang) }), icon: 'trophy' as const } : null;
    }
    case 'battlepassLevelUp':
      return { text: translate(lang, 'pass.levelUp', { tier: event.to + 1 }), icon: 'level' as const };
    default:
      return null;
  }
}

function CurrencyPill({ icon, value, title, desc, extra, go }: { icon: 'fakecoin' | 'lootcoin' | 'energy'; value: ReactNode; title: string; desc: string; extra?: ReactNode; go?: { label: string; route: RouteName } }) {
  return (
    <Popover
      label={title}
      trigger={(open, toggle) => (
        <button type="button" className={`pill ${open ? 'is-open' : ''}`} onClick={toggle} aria-expanded={open} aria-label={title}>
          <Sprite asset={{ kind: 'ui', name: icon }} size={20} decorative />
          <span className="pill__value">{value}</span>
        </button>
      )}
    >
      <span className="popover__title">
        <Sprite asset={{ kind: 'ui', name: icon }} size={22} decorative /> {title}
      </span>
      <span className="popover__text">{desc}</span>
      {extra}
      {go && (
        <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(go.route)}>
          {go.label}
        </button>
      )}
    </Popover>
  );
}

function TopBar() {
  const save = useSave();
  const t = useT();
  const now = useNow();
  const energy = useLiveEnergy();
  const need = xpForNextLevel(save.level.level);
  const lead = leadCharacter(save);
  const lang = useLang();
  const fmt = (n: number) => formatNumber(lang, n);
  const mission = missionStatus(save, now);
  const missionEnd = missionEndsAt(save);
  const nextEnergy = nextEnergyAtLive(save, now);

  return (
    <header className="topbar">
      <button type="button" className="profile" onClick={() => navigate('stats')} aria-label={`${save.username} · ${t('common.level', { level: save.level.level })}`}>
        <Ring value={save.level.xp} max={need} size={46} stroke={4} color="var(--xp)">
          <span className="profile__avatar">{lead ? <Sprite asset={{ kind: 'item', type: 'characters', id: lead }} size={36} decorative /> : <span className="profile__initial">{save.username.slice(0, 1).toUpperCase()}</span>}</span>
        </Ring>
        <span className="profile__lvl">{save.level.level}</span>
        <span className="profile__text">
          <span className="profile__name">{save.username}</span>
          <span className="profile__xp">{t('common.xpProgress', { xp: fmt(save.level.xp), need: fmt(need) })}</span>
        </span>
      </button>
      <div className="topbar__right">
        {mission !== 'idle' && missionEnd !== null && (
          <button type="button" className={`chip ${mission === 'done' ? 'chip--ready' : ''}`} onClick={() => navigate('missions')} aria-label={mission === 'done' ? t('topbar.missionDone') : t('topbar.mission')}>
            <Hourglass size={15} strokeWidth={2.4} aria-hidden="true" />
            <span className="chip__text">{mission === 'done' ? t('common.ready') : <Countdown at={missionEnd} compact />}</span>
          </button>
        )}
        <CurrencyPill icon="fakecoin" value={<CountUp value={save.money} format={(n) => formatCompact(lang, n)} />} title={t('currency.fakecoinTitle')} desc={t('currency.fakecoinDesc')} go={{ label: t('nav.collection'), route: 'collection' }} />
        <CurrencyPill icon="lootcoin" value={<CountUp value={save.lootbox} format={(n) => formatCompact(lang, n)} />} title={t('currency.lootcoinTitle')} desc={t('currency.lootcoinDesc')} go={{ label: t('nav.summon'), route: 'summon' }} />
        <CurrencyPill
          icon="energy"
          value={
            <>
              {energy}
              <span className="pill__max">/{save.adventures.energy.max}</span>
            </>
          }
          title={t('currency.energyTitle')}
          desc={t('currency.energyDesc', { max: save.adventures.energy.max })}
          extra={<span className="popover__text popover__text--strong">{nextEnergy ? <>{t('currency.energyNext', { time: '' })}<Countdown at={nextEnergy} /></> : t('currency.energyFull')}</span>}
          go={{ label: t('nav.adventures'), route: 'adventures' }}
        />
        <button type="button" className="iconbtn topbar__settings" onClick={() => navigate('settings')} aria-label={t('nav.settings')}>
          <SettingsIcon size={20} />
        </button>
      </div>
    </header>
  );
}

function Nav({ route }: { route: RouteName }) {
  const save = useSave();
  const session = useSession();
  const t = useT();
  const now = useNow(5000);
  const badges = navBadges(save, session, now);
  const item = (n: NavItem, extra = false) => {
    const b = badges[n.name];
    const Icon = n.icon;
    return (
      <a key={n.name} href={`#/${n.name}`} className={`nav__item ${extra ? 'nav__item--extra' : ''} ${route === n.name ? 'is-active' : ''}`} aria-current={route === n.name ? 'page' : undefined}>
        <span className="nav__icon">
          <Icon size={22} strokeWidth={2} aria-hidden="true" />
          {b?.count ? <Badge count={b.count} tone={n.name === 'summon' ? 'gold' : 'alert'} /> : b?.dot ? <Badge dot /> : null}
        </span>
        <span className="nav__label">{t(n.key)}</span>
      </a>
    );
  };
  return (
    <nav className="nav" aria-label="Navigation">
      <div className="nav__brand" aria-hidden="true">
        <span className="brand-mark">F</span>
        <span className="brand-name">Fake RPG</span>
      </div>
      {NAV_MAIN.map((n) => item(n))}
      <span className="nav__sep" aria-hidden="true" />
      {NAV_EXTRA.map((n) => item(n, true))}
      <a href="#/settings" className={`nav__item nav__item--extra nav__item--bottom ${route === 'settings' ? 'is-active' : ''}`}>
        <span className="nav__icon">
          <SettingsIcon size={22} strokeWidth={2} aria-hidden="true" />
        </span>
        <span className="nav__label">{t('nav.settings')}</span>
      </a>
    </nav>
  );
}

/** Online, read path only (T3.7): the account is shown, actions arrive with the action API (T3.8). */
function ActionsSoon() {
  const t = useT();
  return (
    <p className="notice notice--soon" role="status">
      <Hourglass size={16} aria-hidden="true" />
      <span>{t('actions.banner')}</span>
    </p>
  );
}

function Screen({ name }: { name: RouteName }) {
  if (name === 'home') return <Home />;
  const Lazy = lazyScreens[name];
  return <Lazy />;
}

function Shell() {
  const route = useRoute();
  const store = useStore();
  const prefs = usePrefs();
  const busy = useSnapshot().busy;

  // The account lives on the server (and changes on Discord too): reload it every minute while the
  // page is visible, when it comes back to the foreground and when the network returns. A 401 here
  // means the session ended: the store goes back to the login screen.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') void store.load();
    };
    const id = setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [store]);

  useEffect(() => {
    window.scrollTo?.({ top: 0 });
  }, [route.name]);

  // Warm the other screens' chunks once the Camp is up (the service worker caches them too).
  useEffect(() => {
    const id = setTimeout(prefetchScreens, 1500);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className={`app ${prefs.reducedMotion ? 'app--still' : ''} ${busy ? 'app--busy' : ''}`} aria-busy={busy}>
      <div className="backdrop" aria-hidden="true" />
      <Nav route={route.name} />
      <div className="app__main">
        <TopBar />
        <PwaNotices />
        {!store.actionsEnabled && <ActionsSoon />}
        <main className="content" id="main" key={route.name}>
          <Suspense fallback={<div className="page page--loading" aria-busy="true" />}>
            <Screen name={route.name} />
          </Suspense>
        </main>
      </div>
      <LevelUpOverlay />
      <Toasts />
    </div>
  );
}

/** Page backgrounds (tokens.css `--bg`) for the browser's theme-color. */
const THEME_BG = { dark: '#0d0e1c', light: '#f3ede1' } as const;

export function App() {
  const { phase, state, prefs } = useSnapshot();
  const store = useStore();
  const { theme, sound, lang } = prefs;

  useEffect(() => {
    store.formatToast = toastFor;
    store.formatError = (code, params, l) => errorText(l, code, params);
    store.formatMessage = (key, l) => translate(l, key);
  }, [store]);

  useEffect(() => setSoundEnabled(sound), [sound]);

  // Language switched elsewhere: fetch its UI strings, then repaint.
  const [, setLangTick] = useState(0);
  useEffect(() => {
    if (!isLangLoaded(lang)) void loadLang(lang).then(() => setLangTick((n) => n + 1));
  }, [lang]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') delete root.dataset['theme'];
    else root.dataset['theme'] = theme;
    root.lang = lang;
    // Browser/installed-app chrome follows the in-game theme, not only the OS one.
    for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
      const scheme = theme === 'system' ? (meta.media.includes('light') ? 'light' : 'dark') : theme;
      meta.content = THEME_BG[scheme];
    }
  }, [theme, lang]);

  return phase === 'ready' && state ? <Shell /> : <Gate phase={phase === 'ready' ? 'loading' : phase} />;
}
